import { createSelector, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { Alignment, WidgetSize, WidgetTrackingForm } from '@/constants/enum';

interface ICourierMapping {
  courierList: Array<string>;
}

interface ITrackingLink {
  replaceCourierLink: boolean;
  linkDescription: string;
  addLinkToOrder: boolean;
}

export interface IOrderLookup {
  widgeTitle: string;
  widgetTracking: WidgetTrackingForm;
  emailRequired: boolean;
  btnLabel: string;
  btnBackgroundColor: string;
  btnTextColor: string;
  btnRadius: number;
  widgetSize: WidgetSize;
  alignment: Alignment;
  isShowTrackingResult: boolean;
}

interface IIntegration {
  isCollapse: boolean;
  isConnected: boolean;
  appID: { value: string; error: string };
  secretKey: { value: string; error: string; isShow: boolean };
}

interface IIntegrationTrustpilot {
  isCollapse: boolean;
  isConnected: boolean;
  isActive: boolean;
  businessUnitId: { value: string; error: string };
  clientId: { value: string; error: string };
  clientSecret: { value: string; error: string; isShow: boolean };
  businessUserId: { value: string; error: string };
}

export interface ISettingsState {
  tabSelected: string;
  courierMapping: ICourierMapping;
  trackingLink: ITrackingLink;
  trackingLinkSyncCooldownEnd: number;
  orderLookup: IOrderLookup;
  integration: IIntegration;
  integrationTrustpilot: IIntegrationTrustpilot;
}

// Define the initial state using that type
const initialState: ISettingsState = {
  tabSelected: 'trackingLink',
  courierMapping: {
    courierList: [],
  },
  trackingLink: {
    replaceCourierLink: false,
    linkDescription: '',
    addLinkToOrder: false,
  },
  trackingLinkSyncCooldownEnd: 0,
  orderLookup: {
    widgeTitle: '',
    widgetTracking: WidgetTrackingForm.Both,
    emailRequired: false,
    btnLabel: 'Track',
    btnBackgroundColor: '#EA3E34',
    btnTextColor: '#FFFFFF',
    btnRadius: 10,
    widgetSize: WidgetSize.Medium,
    alignment: Alignment.Center,
    isShowTrackingResult: false,
  },
  integration: {
    isCollapse: false,
    isConnected: false,
    appID: { value: '', error: '' },
    secretKey: { value: '', error: '', isShow: false },
  },
  integrationTrustpilot: {
    isCollapse: false,
    isConnected: false,
    isActive: true,
    businessUnitId: { value: '', error: '' },
    clientId: { value: '', error: '' },
    clientSecret: { value: '', error: '', isShow: false },
    businessUserId: { value: '', error: '' },
  },
};

export const settingsSlice = createSlice({
  name: 'settings',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    handleSelected: (state, action: PayloadAction<string>) => {
      state.tabSelected = action.payload;
    },
    handleCourierMapping: (state, action: PayloadAction<ICourierMapping>) => {
      state.courierMapping = action.payload;
    },
    handleTrackingLink: (state, action: PayloadAction<ITrackingLink>) => {
      state.trackingLink = action.payload;
    },
    handleTrackingLinkSyncCooldownEnd: (state, action: PayloadAction<number>) => {
      state.trackingLinkSyncCooldownEnd = action.payload;
    },
    handleOrderLookup: (state, action: PayloadAction<IOrderLookup>) => {
      state.orderLookup = action.payload;
    },
    handleChangeIntegration: (state, action: PayloadAction<IIntegration>) => {
      state.integration = action.payload;
    },
    handleChangeIntegrationTrustpilot: (state, action: PayloadAction<IIntegrationTrustpilot>) => {
      state.integrationTrustpilot = action.payload;
    },
  },
});

// Other code such as selectors can use the imported `RootState` type
export const tabSelectedSelector = createSelector(
  (state: RootState) => state.settings,
  (state) => state.tabSelected,
);

export const courierMappingSelector = createSelector(
  (state: RootState) => state.settings,
  (state) => state.courierMapping,
);

export const trackingLinkSelector = createSelector(
  (state: RootState) => state.settings,
  (state) => state.trackingLink,
);

export const trackingLinkSyncCooldownEndSelector = createSelector(
  (state: RootState) => state.settings,
  (state) => state.trackingLinkSyncCooldownEnd,
);

export const orderLookupSelector = createSelector(
  (state: RootState) => state.settings,
  (state) => state.orderLookup,
);

export const integrationSelector = createSelector(
  (state: RootState) => state.settings,
  (state) => state.integration,
);

export const integrationTrustpilotSelector = createSelector(
  (state: RootState) => state.settings,
  (state) => state.integrationTrustpilot,
);

export default settingsSlice;
